import { useCallback, useMemo } from 'react';
import { useOverlayContext, useStateInRef } from 'seagull';
import { useClientContactModals } from '~/components/AgreementStudioPage/AgreementStudioProposalMode/hooks/useClientContactModals';
import { editContactModalContent } from '~/components/ContactModals/contactModals';
import { useContactsMap } from '~/hooks/reactQuery/derived/useContactsMap';
import { useClientContacts } from '~/hooks/reactQuery/queries/useClientContacts';
import { useClients } from '~/hooks/reactQuery/queries/useClients';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { createdClientContactId } from '~/utils/contact-identity.utils';
import { useGetDraftContact } from '../../../../hooks/useGetDraftContact';
import { draftClientId, reviewersWithoutContact } from '../../../draftClient.utils';
export function useMainSignatorySelection(draft, setDraft) {
    const getDraft = useStateInRef(draft);
    const { showModal } = useOverlayContext();
    const { CRM_CONTACTS } = useFeatureFlags();
    const legacyPrimaryContact = useGetDraftContact(draft);
    const clientId = draftClientId(draft, legacyPrimaryContact);
    const { clients } = useClients();
    const { fetchContacts: refreshAllContacts } = useContactsMap();
    const { contacts, isLoading, refresh: refreshClientContacts, getContacts: getClientContacts } = useClientContacts(clientId, {
        enabled: clientId != null,
        refreshOnHookMount: true
    });
    const signatory = useMemo(()=>{
        var _contacts_find;
        return (_contacts_find = contacts.find((contact)=>contact.id() === draft.clientContactId())) !== null && _contacts_find !== void 0 ? _contacts_find : null;
    }, [
        contacts,
        draft
    ]);
    const selectContactById = useCallback((contactId)=>{
        const current = getDraft();
        setDraft(current.setClientContactId(contactId).setReviewers(reviewersWithoutContact(current.reviewers(), contactId)));
    }, [
        setDraft
    ]);
    const refreshContacts = useCallback(()=>Promise.all([
            refreshClientContacts({
                throwOnError: false
            }),
            refreshAllContacts({
                throwOnError: false
            })
        ]), [
        refreshClientContacts,
        refreshAllContacts
    ]);
    const { onCreateContact: openCreateContactModal } = useClientContactModals({
        clients,
        selectedClientId: clientId,
        onClientCreated: ()=>{},
        refreshContacts
    });
    const onCreateContact = ()=>openCreateContactModal(async (createdId)=>{
            var _getClientContacts;
            const selectedId = createdClientContactId({
                contacts: (_getClientContacts = getClientContacts()) !== null && _getClientContacts !== void 0 ? _getClientContacts : [],
                clientId,
                createdId,
                keepUnresolvedId: CRM_CONTACTS !== true
            });
            if (selectedId != null) selectContactById(selectedId);
        });
    const onSelectContact = useCallback((contact)=>{
        var _ref;
        return selectContactById((_ref = contact === null || contact === void 0 ? void 0 : contact.id()) !== null && _ref !== void 0 ? _ref : null);
    }, [
        selectContactById
    ]);
    const onEditContact = useCallback(async ()=>{
        if (clientId != null && signatory != null) {
            await showModal({
                biName: 'edit_contact',
                content: editContactModalContent(clientId, signatory, CRM_CONTACTS === true)
            });
            await refreshContacts();
        }
    }, [
        clientId,
        signatory,
        refreshContacts,
        CRM_CONTACTS
    ]);
    return {
        contacts,
        isLoading,
        signatory,
        onSelectContact,
        onCreateContact,
        onEditContact
    };
}
